/**
 * Permission Service
 * Manages user permissions and access control using path-based hierarchy
 *
 * Permission paths work like filesystem paths:
 * - Shorter paths include longer paths (clients:view includes clients:view:assigned)
 * - Wildcards (*) can be used for explicit matching (clients:*:assigned)
 * - No predefined hierarchy - completely flexible
 */

import {
	type RbacActionDefinition,
	RbacActions,
} from '../constants/rbac-actions';
import { isWorkdUser as checkIsWorkdUser } from '../utils/user';

class PermissionServiceClass {
	private permissions: Set<string> = new Set();
	private userEmail: string | null = null;

	/**
	 * Initialize permissions on application load
	 * @param permissions - Array of permission strings
	 * @param userEmail - Optional user email for Workd user checks
	 */
	initialize(permissions: string[], userEmail?: string): void {
		this.permissions = new Set(permissions);
		this.userEmail = userEmail ?? null;
	}

	/**
	 * Set the user's email (for Workd user checks)
	 * @param email - The user's email address
	 */
	setUserEmail(email: string | null | undefined): void {
		this.userEmail = email ?? null;
	}

	/**
	 * Check if the current user is a Workd/Developrise user
	 * @returns true if the user's email is from @workd.com or @developrise.com
	 */
	isWorkdUser(): boolean {
		return checkIsWorkdUser(this.userEmail);
	}

	/**
	 * Check if the current user has permission to perform an action
	 * Uses path-based matching with implicit hierarchy
	 * @param requiredPermission - The permission to check for (string or RbacActionDefinition)
	 * @returns true if the user has the permission, false otherwise
	 */
	can(requiredPermission: string | RbacActionDefinition): boolean {
		// Extract the action string if RbacActionDefinition is passed
		const permissionString =
			typeof requiredPermission === 'string'
				? requiredPermission
				: requiredPermission.action;

		// Exact match
		if (this.permissions.has(permissionString)) {
			return true;
		}

		// Check for path-based matches (including wildcards)
		return this.hasPathMatch(permissionString);
	}

	/**
	 * Check if user has any of the specified permissions (OR logic)
	 * @param permissions - Array of permissions to check (strings or RbacActionDefinitions)
	 * @returns true if user has at least one permission
	 */
	canAny(permissions: (string | RbacActionDefinition)[]): boolean {
		return permissions.some((p) => this.can(p));
	}

	/**
	 * Check if user has all of the specified permissions (AND logic)
	 * @param permissions - Array of permissions to check (strings or RbacActionDefinitions)
	 * @returns true if user has all permissions
	 */
	canAll(permissions: (string | RbacActionDefinition)[]): boolean {
		return permissions.every((p) => this.can(p));
	}

	/**
	 * Check if user can update a client based on whether it's a lead or customer
	 * @param leadOrCustomer - The client's leadOrCustomer field value ('Lead' or 'Customer')
	 * @returns true if user has permission to update this client type
	 */
	canUpdateClient(leadOrCustomer: string | null | undefined): boolean {
		const isLead = leadOrCustomer?.toLowerCase() === 'lead';

		return isLead
			? this.can(RbacActions.CLIENTS.LEADS.UPDATE)
			: this.can(RbacActions.CLIENTS.CUSTOMERS.UPDATE);
	}

	/**
	 * Check if the user holds any permission whose string starts with the given
	 * prefix. Unlike {@link can}, this matches LONGER paths under a prefix (the
	 * opposite of the implicit "shorter grants longer" hierarchy) — e.g.
	 * `canAnyMatching('reporting:custom:view:')` is true when the user holds any
	 * `reporting:custom:view:<reportKey>` action. Used to show a nav section when
	 * the user can access at least one of a dynamic, data-driven set of actions
	 * (custom reports) that have no single static parent action.
	 * @param prefix - The permission-string prefix to match (typically ending in ':')
	 * @returns true if any held permission starts with the prefix
	 */
	canAnyMatching(prefix: string): boolean {
		for (const perm of this.permissions) {
			if (perm.startsWith(prefix)) {
				return true;
			}
		}
		return false;
	}

	/**
	 * Get all user permissions
	 * @returns Array of permission strings
	 */
	getPermissions(): string[] {
		return Array.from(this.permissions);
	}

	/**
	 * Clear all permissions
	 */
	clearPermissions(): void {
		this.permissions.clear();
	}

	/**
	 * Check for path-based permission matches
	 * Handles both implicit hierarchy and explicit wildcards
	 */
	private hasPathMatch(requiredPermission: string): boolean {
		const requiredParts = requiredPermission.split(':');

		// Special case: permissions ending with ':self' should NOT be granted by parent permissions
		// e.g., 'user:companies:update' should NOT grant 'user:companies:update:self'
		const endsWithSelf = requiredParts[requiredParts.length - 1] === 'self';

		for (const userPerm of this.permissions) {
			// Handle explicit wildcards (e.g., clients:*:assigned or *)
			if (userPerm.includes('*')) {
				if (this.wildcardMatches(userPerm, requiredPermission)) {
					return true;
				}
			}

			// Handle implicit hierarchy (shorter paths include longer paths)
			// e.g., "clients:view" includes "clients:view:assigned"
			// BUT: "user:companies:update" does NOT include "user:companies:update:self"
			const userParts = userPerm.split(':');
			if (userParts.length < requiredParts.length && !endsWithSelf) {
				const requiredPrefix = requiredParts
					.slice(0, userParts.length)
					.join(':');
				if (userPerm === requiredPrefix) {
					return true;
				}
			}
		}

		return false;
	}

	/**
	 * Check if a wildcard pattern matches the required permission
	 */
	private wildcardMatches(pattern: string, permission: string): boolean {
		// Convert wildcard pattern to regex
		// Replace * with .* and escape other special regex characters
		const regexPattern = pattern
			.split(':')
			.map((part) => {
				if (part === '*') {
					return '.*';
				}
				// Escape special regex characters except *
				return part.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
			})
			.join(':');

		const regex = new RegExp(`^${regexPattern}$`);
		return regex.test(permission);
	}
}

// Export singleton instance
export const PermissionService = new PermissionServiceClass();
